// @mui material components
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import { alpha } from "@mui/material/styles";
import EditOutlined from "@mui/icons-material/EditOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import MapOutlined from "@mui/icons-material/MapOutlined";
import PersonOutlined from "@mui/icons-material/PersonOutlined";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Icon from "@mui/material/Icon";
import { Link, useNavigate } from "react-router-dom";
import { teacherDetailPath } from "utils/teacherRoutes";
import TeacherProfileLink from "components/TeacherProfileLink";
import TeacherAvatar from "components/TeacherAvatar";
import { format } from "date-fns";

import Card from "@mui/material/Card";
// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
// Soft UI Dashboard React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { useFormik } from "formik";
import * as Yup from "yup";
import Select2Field from 'components/Select2Field';
import { Grid } from '@mui/material';
import Collapse from "@mui/material/Collapse";
import debounce from 'debounce';
import Swal from 'sweetalert2';
import IconButton from "@mui/material/IconButton";




import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { apiurl } from "utils";
import Loading from 'components/Loading';
import TablePaginationBar from "components/TablePaginationBar";
import api from 'services/api';
import { useTranslate } from "utils/i18n";
import { readIsTeacher, readIsLicensed } from "utils/teacherFlags";
import { useRegisterAssistantPage } from "assistant/useRegisterAssistantPage";
import {
  parseListPayload,
  totalPagesFromCount,
} from "utils/paginationStats";
import {
  EMPTY_TEACHER_FILTERS,
  fetchTeacherList,
  fetchTeacherListForExport,
} from "services/teacherListService";
import {
  figmaTokens,
  directoryTableHeaderCellSx,
  directoryTableHeadStickyOverridesSx,
  directoryTableBodyCellDefaultsSx,
} from "assets/theme/designTokens";
import { useSoftUIController } from "context";
import { isObserver } from "assistant/userRole";
import {
  MAP_TEACHER_DEGREES,
  exportTeacherRegistryToExcel,
  isWithinRadius,
} from "layouts/newmap/mapHelpers";

const CARD_SHELL_SX = {
  overflow: "hidden",
  borderRadius: "16px",
  border: `1px solid ${figmaTokens.borderSubtle}`,
  boxShadow: "0 14px 44px rgba(15, 23, 42, 0.07)",
  bgcolor: figmaTokens.surface,
};

function pctOf(part, total) {
  const t = Math.max(0, Number(total) || 0);
  const p = Math.max(0, Number(part) || 0);
  if (t <= 0) return 0;
  return Math.round((p / t) * 100);
}

const filterLabelSx = {
  display: "block",
  mb: 0.5,
  fontWeight: 700,
  fontSize: "0.68rem",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: figmaTokens.textMuted,
};

const INITIAL_CATEGORY_COUNTS = { 1: 0, 2: 0, 3: 0, 4: 0 };

const PIPELINE_SEGMENT_COLORS = {
  notAssigned: "#f59e0b",
  assignedPending: "#667eea",
  withAttendance: "#10b981",
};

const teachersTableShellSx = {
  mx: { xs: 1.25, md: 2 },
  mb: 0,
  borderRadius: "14px",
  border: `1px solid ${figmaTokens.borderSubtle}`,
  overflow: "auto",
  maxHeight: { xs: "65vh", md: "min(68vh, 680px)" },
  bgcolor: figmaTokens.surface,
  boxShadow: "0 10px 40px rgba(15, 23, 42, 0.07)",
};

/** Gender pill — aligned with app chip scale (height 26, 8px radius) */
function teacherGenderChipSx(raw) {
  const s = String(raw ?? "").trim().toLowerCase();
  const base = {
    fontWeight: 700,
    fontSize: "0.72rem",
    height: 26,
    borderRadius: "8px",
    "& .MuiChip-label": { px: 1.25 },
  };
  if (!s || s === "—") {
    return {
      ...base,
      bgcolor: figmaTokens.pageBg,
      color: figmaTokens.textMuted,
      border: `1px solid ${figmaTokens.borderSubtle}`,
    };
  }
  if (s.includes("hom") || s === "h" || s === "m" || s.includes("male")) {
    return {
      ...base,
      bgcolor: "rgba(102, 126, 234, 0.14)",
      color: "#4338ca",
      border: "1px solid rgba(102, 126, 234, 0.35)",
    };
  }
  if (s.includes("fem") || s === "f" || s.includes("female")) {
    return {
      ...base,
      bgcolor: "rgba(219, 39, 119, 0.1)",
      color: "#be185d",
      border: "1px solid rgba(219, 39, 119, 0.28)",
    };
  }
  return {
    ...base,
    bgcolor: figmaTokens.pageBg,
    color: figmaTokens.textSecondary,
    border: `1px solid ${figmaTokens.borderStrong}`,
    fontWeight: 600,
  };
}

/** Status pill — same footprint as gender chips */
function teacherStatusChipSx(raw) {
  const s = String(raw ?? "").trim().toLowerCase();
  const base = {
    fontWeight: 700,
    fontSize: "0.72rem",
    height: 26,
    borderRadius: "8px",
    "& .MuiChip-label": { px: 1.25 },
  };
  if (!s || s === "—") {
    return {
      ...base,
      bgcolor: figmaTokens.pageBg,
      color: figmaTokens.textMuted,
      border: `1px solid ${figmaTokens.borderSubtle}`,
    };
  }
  if (s.includes("inact") || s.includes("suspend")) {
    return {
      ...base,
      color: "#fff",
      bgcolor: "#dc2626",
      border: "none",
      boxShadow: "0 2px 8px rgba(220, 38, 38, 0.28)",
    };
  }
  if (s.includes("act") || s === "actif" || s === "1" || s === "active") {
    return {
      ...base,
      color: "#fff",
      background: figmaTokens.gradientCSS,
      border: "none",
      boxShadow: "0 3px 10px rgba(102, 126, 234, 0.32)",
    };
  }
  return {
    ...base,
    bgcolor: figmaTokens.pageBg,
    color: figmaTokens.textSecondary,
    border: `1px solid ${figmaTokens.borderStrong}`,
    fontWeight: 600,
  };
}

function teacherLicenseChipSx(isLicensed) {
  const base = {
    fontWeight: 700,
    fontSize: "0.72rem",
    height: 26,
    borderRadius: "8px",
    "& .MuiChip-label": { px: 1.25 },
  };
  if (isLicensed) {
    return {
      ...base,
      color: "#fff",
      bgcolor: "#16a34a",
      border: "none",
      boxShadow: "0 2px 8px rgba(22, 163, 74, 0.28)",
    };
  }
  return {
    ...base,
    color: "#92400e",
    bgcolor: "#fef3c7",
    border: "1px solid #fbbf24",
  };
}

function teacherTypeChipSx(isTeacher) {
  const base = {
    fontWeight: 700,
    fontSize: "0.72rem",
    height: 26,
    borderRadius: "8px",
    "& .MuiChip-label": { px: 1.25 },
  };
  if (isTeacher) {
    return {
      ...base,
      bgcolor: "rgba(102, 126, 234, 0.14)",
      color: figmaTokens.accent,
      border: `1px solid rgba(102, 126, 234, 0.25)`,
    };
  }
  return {
    ...base,
    bgcolor: "rgba(148, 163, 184, 0.16)",
    color: figmaTokens.textSecondary,
    border: `1px solid ${figmaTokens.borderSubtle}`,
  };
}

/** Boolean pill for assigned / attendance columns */
function teacherBoolChipSx(isYes) {
  const base = {
    fontWeight: 700,
    fontSize: "0.72rem",
    height: 26,
    borderRadius: "8px",
    "& .MuiChip-label": { px: 1.25 },
  };
  if (isYes) {
    return {
      ...base,
      color: "#fff",
      background: figmaTokens.gradientCSS,
      border: "none",
      boxShadow: "0 3px 10px rgba(102, 126, 234, 0.32)",
    };
  }
  return {
    ...base,
    bgcolor: figmaTokens.pageBg,
    color: figmaTokens.textMuted,
    border: `1px solid ${figmaTokens.borderSubtle}`,
  };
}

const TEACHER_CATEGORIES = [
  {
    value: 1,
    labelKey: "teachers.category.all",
    fallback: "All teachers",
    descKey: "teachers.category.all_desc",
    descFallback: "Every enrolled teacher in the system.",
    icon: "groups",
    accent: "#667eea",
  },
  {
    value: 2,
    labelKey: "teachers.category.not_assigned",
    fallback: "Enrolled, not assigned",
    descKey: "teachers.category.not_assigned_desc",
    descFallback: "Registered but not yet linked to an establishment.",
    icon: "person_off",
    accent: "#f59e0b",
  },
  {
    value: 3,
    labelKey: "teachers.category.assigned",
    fallback: "Enrolled and assigned",
    descKey: "teachers.category.assigned_desc",
    descFallback: "Linked to at least one establishment.",
    icon: "assignment_ind",
    accent: "#6366f1",
  },
  {
    value: 4,
    labelKey: "teachers.category.assigned_with_attendance",
    fallback: "Assigned with attendance",
    descKey: "teachers.category.assigned_with_attendance_desc",
    descFallback: "Assigned and has at least one attendance recorded.",
    icon: "fact_check",
    accent: "#10b981",
  },
];

function Enseignantes() {
  const [controller] = useSoftUIController();
  const readOnly = isObserver(controller.user);
  const [teachers, setTeachers] = useState([]);
  const [recruiterOptions, setRecruiterOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [category, setCategory] = useState(1);
  const [totalTeachers, setTotalTeachers] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [categoryCounts, setCategoryCounts] = useState(INITIAL_CATEGORY_COUNTS);
  const [draftFilters, setDraftFilters] = useState(EMPTY_TEACHER_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_TEACHER_FILTERS);
  const [activeApiFilters, setActiveApiFilters] = useState(null);
  const [filtersMinimized, setFiltersMinimized] = useState(false);
  const [filtersAdvancedOpen, setFiltersAdvancedOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [attendanceSummary, setAttendanceSummary] = useState(() => new Map());
  const { t } = useTranslate();

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${localStorage.getItem("tk446655440000")}`,
    }),
    []
  );

  const fetchTeachers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchTeacherList({
        pageNumber: page,
        pageSize,
        category,
        filters: appliedFilters,
      });
      setTeachers(result.rows);
      setTotalTeachers(result.stats.totalTeachers || result.stats.totalResults || 0);
      setTotalResults(result.stats.totalResults || 0);
      setCategoryCounts(result.categoryCounts);
      setActiveApiFilters(result.activeFilters);
    } catch (err) {
      console.error("API Error:", err);
      setTeachers([]);
      setTotalTeachers(0);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, category, appliedFilters]);

  const refreshAll = useCallback(async () => {
    await fetchTeachers();
  }, [fetchTeachers]);

  const fetchRecruiters = async () => {
    try {
      const res = await api.get("/Recruiters/list", {
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("tk446655440000")}`,
        },
      });
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data?.items)
            ? res.data.items
            : [];
      setRecruiterOptions(list.map((item) => ({
        id: item.id,
        text: item.name || item.userName || String(item.id),
      })));
    } catch (err) {
      console.error("Recruiters API Error:", err);
      setRecruiterOptions([]);
    }
  };

  useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]);

  useEffect(() => {
    if (!readOnly) fetchRecruiters();
  }, [readOnly]);

  useEffect(() => {
    if (readOnly) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/Teacher/scoresummary", {
          params: { EstabId: "", pageNumber: 1, pageSize: 5000 },
          headers: authHeaders,
        });
        const list = parseListPayload(res.data);
        const map = new Map();
        list.forEach((row) => {
          const id = String(row.id ?? row.Id ?? row.teacherId ?? row.TeacherId ?? "");
          if (!id) return;
          const raw = row.attendDateTime ?? row.AttendDateTime ?? "";
          const prev = map.get(id);
          if (!prev || String(raw) > String(prev.lastCheckIn)) {
            map.set(id, {
              lastCheckIn: raw,
              inRadius: isWithinRadius(row, 500),
            });
          }
        });
        if (!cancelled) setAttendanceSummary(map);
      } catch (err) {
        console.error("Attendance summary error", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authHeaders, readOnly]);


  const formik = useFormik({
      initialValues: {
        name: "",
        preName: "",
        gender: "",
        regDate: "",
        degree: "",
        phone: "",
        dob: "",
        status: "",
        certificate: "",
        regNumber: "",
        identificationNumber: "",
        integrationOrderNumber: "",
        embedding: "",
        recruiterId: "",
        isTeacher: "true",
      },
      validationSchema: Yup.object({
        name: Yup.string().required("Nom est requis"),
        preName: Yup.string().required("Prénom est requis"),
        gender: Yup.string().required("Genre est requis"),
        regDate: Yup.string().required("Date d'enregistrement est requise"),
        dob: Yup.string().required("Date de naissance est requise"),
        status: Yup.string().required("Statut est requis"),
        certificate: Yup.string().required("Certificat est requis"),
        regNumber: Yup.string().required("Numéro d'enregistrement est requis"),
        identificationNumber: Yup.string().required("Numéro d'identification est requis"),
        integrationOrderNumber: Yup.string().required("Numéro de commande d'intégration est requis"),
        phone: Yup.string().required("Numéro de Téléphone est requis"),
      }),
      onSubmit: async (values, {setSubmitting, resetForm}) => {
        try {
          console.log("Form submitted:", JSON.stringify(values));
          setLoading(true);
          const formData = new FormData();
          formData.append("Name", values.name);
          formData.append("PreName", values.preName);
          formData.append("Gender", values.gender);
          formData.append("RegDate", values.regDate);
          formData.append("Degree", values.degree);
          formData.append("DOB", values.dob);
          formData.append("Status", values.status);
          formData.append("Certificate", values.certificate);
          formData.append("RegNumber", edit ? "" : values.regNumber);
          formData.append("IdentificationNumber", values.identificationNumber);
          formData.append("IntegrationOrderNumber", values.integrationOrderNumber);
          formData.append("Phone", values.phone);
          formData.append("Role", values.role);
          formData.append("Embedding", values.embedding);
          formData.append("RecruiterId", values.recruiterId);
          formData.append("IsTeacher", values.isTeacher === "true" ? "true" : "false");

          if(edit === true) {
            formData.append("id", editData.id);
            const response = await axios.post(apiurl + `Teacher/addedit?Id=${editData.id}`, formData, {
              headers: {
                "Authorization": `Bearer ${localStorage.getItem("tk446655440000")}`
              }
            });
            const isSuccess = response?.data?.success === true || response?.data === true;
            if (isSuccess) {
              Swal.fire({
                icon: 'success',
                title: 'Utilisateur mis à jour',
                text: 'L\'utilisateur a été mis à jour avec succès.',
                showConfirmButton: false,
                timer: 2000
              });
              refreshAll();
              setEdit(false);
              setEditData(null);
              resetForm();
            }
          } else{
            const response = await axios.post(apiurl + "Teacher/addedit", formData, {
            headers: {
              "Authorization": `Bearer ${localStorage.getItem("tk446655440000")}`
              }
            });
            const isSuccess = response?.data?.success === true || response?.data === true;
            if (isSuccess) {
              const isTeacherReg = values.isTeacher === "true";
              const successMessage =
                response?.data?.message ??
                (isTeacherReg
                  ? t(
                      "teachers.register_success",
                      "Teacher registered successfully. Awaiting license activation."
                    )
                  : t("teachers.register_staff_success", "Staff member registered successfully."));
              Swal.fire({
                icon: 'success',
                title: isTeacherReg
                  ? t("teachers.register_success_title", "Teacher registered")
                  : t("teachers.register_staff_success_title", "Staff registered"),
                text: successMessage,
                showConfirmButton: false,
                timer: 2500
              });
              refreshAll();
              resetForm();
            }
          }

        } catch (error) {
          console.error("Error creating utilisateur:", error);
          const statusCode = error?.response?.status;
          if (statusCode === 409) {
            Swal.fire({
              icon: 'error',
              title: 'Erreur',
              text: 'Matricule déjà pris.',
            });
          } else {
          Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: 'Une erreur s\'est produite lors de la création de l\'utilisateur.',
          });
          }
        }
        // console.log("Form submitted:", values);
        // alert(JSON.stringify(values, null, 2));
        setLoading(false);
        setSubmitting(false);
      },
    });

    const normalizeGender = (val) => {
      if (!val) return "";
      if (["H", "Homme"].includes(val)) return "Homme";
      if (["F", "Femme"].includes(val)) return "Femme";
      return "";
    };

    const normalizeStatus = (val) => {
      if (!val) return "";
      if ([1, "Active"].includes(val)) return "Active";
      if ([0, "Inactive"].includes(val)) return "Inactive";
      return "";
    };

    const handleEdit = (teacher) => {
      setEdit(true);
      setEditData(teacher);
      setFormOpen(true);
      formik.setValues({
        ...teacher,
        regDate: teacher.regDate ? teacher.regDate.split("T")[0] : "",
        dob: teacher.dob ? teacher.dob.split("T")[0] : "",
        gender: teacher.gender ? String(teacher.gender) : "",
        status: teacher.status ? String(teacher.status) : "",
        recruiterId: teacher.recruiterId ? String(teacher.recruiterId) : (teacher.RecruiterId ? String(teacher.RecruiterId) : ""),
        isTeacher: readIsTeacher(teacher.isTeacher ?? teacher.IsTeacher) ? "true" : "false",
        // gender: normalizeGender(teacher.gender),
        // status: normalizeStatus(teacher.status),
      });
      window.scrollTo({ top: 0, behavior: "smooth" }); // Scroll to top
    };

    const handleDelete = debounce(async (id) => {
      // const confirmed = window.confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?");
      // if (!confirmed) return;

      const result = await Swal.fire({
        icon: 'warning',
        title: 'Confirmation de suppression',
        text: 'Êtes-vous sûr de vouloir supprimer cet utilisateur ?',
        showCancelButton: true,
        confirmButtonText: 'Oui, supprimer',
        cancelButtonText: 'Annuler'
      });
      
      if(result.isConfirmed){
      setLoading(true);
      try {
        await axios.delete(apiurl + `Teacher/addedit`, {id: id}, {
          headers: {
            "Authorization": `Bearer ${localStorage.getItem("tk446655440000")}`
          }
        });
        Swal.fire({
          icon: 'success',
          title: 'Utilisateur supprimé',
          text: 'L\'utilisateur a été supprimé avec succès.',
          showConfirmButton: false,
          timer: 2000
        });
        refreshAll();
      } catch (error) {
        console.error("Error deleting utilisateur:", error);
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Une erreur s\'est produite lors de la suppression de l\'utilisateur.',
        });
        setLoading(false);
      }
    }
      
    }, 300);

  const teachersList = Array.isArray(teachers) ? teachers : [];

  const appliedFilterCount = useMemo(
    () => Object.values(appliedFilters).filter((v) => String(v ?? "").trim()).length,
    [appliedFilters]
  );

  const listFilterActive = appliedFilterCount > 0;

  const updateDraftFilter = (name, value) => {
    setDraftFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...draftFilters });
    setPage(1);
  };

  const resetFilters = () => {
    setDraftFilters(EMPTY_TEACHER_FILTERS);
    setAppliedFilters(EMPTY_TEACHER_FILTERS);
    setPage(1);
  };

  const handleExportFiltered = async () => {
    setLoading(true);
    try {
      const result = await fetchTeacherListForExport({
        category,
        filters: appliedFilters,
      });
      const stamp = format(new Date(), "yyyy-MM-dd");
      exportTeacherRegistryToExcel(
        result.rows,
        `teachers-${stamp}.xlsx`,
        attendanceSummary
      );
    } catch (err) {
      console.error("Export error:", err);
      Swal.fire({
        icon: "error",
        title: t("teachers.export_error", "Export failed"),
        text: t("teachers.export_error_hint", "Could not load teachers for export."),
      });
    } finally {
      setLoading(false);
    }
  };

  const activeFilterChips = useMemo(() => {
    if (!activeApiFilters) return [];
    return Object.entries(activeApiFilters)
      .filter(([, v]) => v != null && String(v).trim())
      .map(([key, value]) => ({ key, value: String(value) }));
  }, [activeApiFilters]);

  const totalPages = totalPagesFromCount(totalResults, pageSize);
  const activeCategory =
    TEACHER_CATEGORIES.find((c) => c.value === category) ?? TEACHER_CATEGORIES[0];

  const enrolledTotal = totalTeachers || categoryCounts[1] || 0;
  const notAssignedCount = categoryCounts[2] ?? 0;
  const assignedCount = categoryCounts[3] ?? 0;
  const withAttendanceCount = categoryCounts[4] ?? 0;
  const assignedPendingAttendance = Math.max(0, assignedCount - withAttendanceCount);
  const showStatusColumns = category === 1;
  const tableColSpan = showStatusColumns ? 16 : 14;

  const assistantPageSnapshot = useMemo(
    () => ({
      pageKey: "enseignantes",
      route: "/enseignantes",
      filters: { category, page, pageSize, totalResults, totalTeachers, appliedFilters, activeApiFilters },
      selection: editData
        ? {
            teacherId: editData.id,
            regNumber: editData.regNumber,
            name: [editData.name, editData.preName].filter(Boolean).join(" "),
          }
        : null,
    }),
    [category, page, pageSize, totalResults, totalTeachers, editData, appliedFilters, activeApiFilters]
  );
  useRegisterAssistantPage(assistantPageSnapshot);

  const pipelineSegments = useMemo(() => {
    const barTotal = Math.max(enrolledTotal, notAssignedCount + assignedCount, 1);
    return [
      {
        key: "notAssigned",
        count: notAssignedCount,
        pct: pctOf(notAssignedCount, barTotal),
        color: PIPELINE_SEGMENT_COLORS.notAssigned,
        labelKey: "teachers.pipeline.legend_not_assigned",
        fallback: "Not assigned",
      },
      {
        key: "assignedPending",
        count: assignedPendingAttendance,
        pct: pctOf(assignedPendingAttendance, barTotal),
        color: PIPELINE_SEGMENT_COLORS.assignedPending,
        labelKey: "teachers.pipeline.legend_assigned_pending",
        fallback: "Assigned, no attendance yet",
      },
      {
        key: "withAttendance",
        count: withAttendanceCount,
        pct: pctOf(withAttendanceCount, barTotal),
        color: PIPELINE_SEGMENT_COLORS.withAttendance,
        labelKey: "teachers.pipeline.legend_with_attendance",
        fallback: "Assigned with attendance",
      },
    ];
  }, [enrolledTotal, notAssignedCount, assignedCount, assignedPendingAttendance, withAttendanceCount]);

  const viewingLabel = t("teachers.pipeline.viewing", "Showing {{count}} teachers — {{label}}")
    .replace("{{count}}", String(totalResults))
    .replace("{{label}}", t(activeCategory.labelKey, activeCategory.fallback));

  const shareLabel = (count) =>
    t("teachers.pipeline.share", "{{pct}}% of enrolled").replace("{{pct}}", String(pctOf(count, enrolledTotal)));

  const teachersTableRangeLabel =
    totalResults === 0
      ? null
      : t("teachers.table_range", "{{start}}–{{end}} sur {{total}} enseignants")
          .replace("{{start}}", String((page - 1) * pageSize + 1))
          .replace("{{end}}", String(Math.min(page * pageSize, totalResults)))
          .replace("{{total}}", String(totalResults));

  const handlePageChange = (value) => {
    setPage(value);
  };

  const handlePageSizeChange = (nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  };

  const handleCategoryChange = (nextCategory) => {
    setCategory(nextCategory);
    setPage(1);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") handleApplyFilters();
  };

  const isTruthyFlag = (val) =>
    val === true || val === 1 || val === "1" || String(val).toLowerCase() === "true";

  const getGenderLabel = (gender) => {
    const g = String(gender ?? "").trim();
    return g || "—";
  };

  const contactCellValue = (row, ...keys) => {
    for (const key of keys) {
      const v = row?.[key];
      if (v != null && String(v).trim()) return String(v).trim();
    }
    return "—";
  };

  return (
    <DashboardLayout>
      {loading && <Loading />}
      <DashboardNavbar />
        <SoftBox py={3} className="content-area-main" sx={{ typography: "body2", bgcolor: figmaTokens.pageBg }}>
          {!readOnly && <Card sx={{ ...CARD_SHELL_SX, mb: 3 }}>
            <SoftBox
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              p={3}
              pb={2}
              gap={2}
            >
              <SoftBox>
                <SoftTypography variant="h6" fontWeight="bold" sx={{ letterSpacing: "-0.02em" }}>
                  {t("teachers.title", "Ajouter un nouvel enseignant")}
                </SoftTypography>
                <SoftTypography variant="caption" color="text" sx={{ display: "block", mt: 0.5, opacity: 0.85, letterSpacing: "0.02em" }}>
                  {t("teachers.form_hint", "Créez ou mettez à jour une fiche enseignant.")}
                </SoftTypography>
              </SoftBox>
              <SoftButton
                variant="outlined"
                color="info"
                size="small"
                sx={{ minWidth: 40, fontWeight: 700, flexShrink: 0 }}
                onClick={() => setFormOpen((prev) => !prev)}
              >
                {formOpen ? "−" : "+"}
              </SoftButton>
            </SoftBox>
            <Collapse in={formOpen} timeout="auto" unmountOnExit>
            <form onSubmit={formik.handleSubmit} className='assignment-form'>
              <Grid container spacing={2}>
                <Grid item xs={12} xl={3}>
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    {t("teachers.person_type", "Person type")}
                  </SoftTypography>
                  <Select2Field
                    name="isTeacher"
                    options={[
                      { id: "true", text: t("teachers.type.teacher", "Teacher") },
                      { id: "false", text: t("teachers.type.staff", "Staff") },
                    ]}
                    value={formik.values.isTeacher}
                    onChange={(val) => formik.setFieldValue("isTeacher", val)}
                    disabled={edit === true}
                  />
                </Grid>
                <Grid item xs={12} xl={3}>
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                      {t("teachers.name", "Nom")}
                  </SoftTypography>
                  <SoftInput type="text" name="name" value={formik.values.name} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                  {formik.touched.name && formik.errors.name ? (
                    <div className="error-message">
                      {formik.errors.name}
                    </div>
                  ) : null}
                </Grid>
                <Grid item xs={12} xl={3}>
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                      {t("teachers.firstname", "Prénom")}
                  </SoftTypography>
                  <SoftInput type="text" name="preName" value={formik.values.preName} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                  {formik.touched.preName && formik.errors.preName ? (
                    <div className="error-message">
                      {formik.errors.preName}
                    </div>
                  ) : null}
                </Grid>
                <Grid item xs={12} xl={3}>
                  <label>{t("teachers.gender", "Sélectionnez le sexe")}</label>
                  <Select2Field
                    name="gender"
                    options={[
                      { id: "", text: t("teachers.gender_placeholder", "Sélectionner le sexe") },
                      { id: "Homme", text: t("gender.male", "Homme") },
                      { id: "Femme", text: t("gender.female", "Femme") },
                    ]}
                    placeholder={t("teachers.gender_placeholder", "Sélectionner le sexe")}
                    value={formik.values.gender}
                    onChange={(val) => {
                      formik.setFieldValue("gender", val);
                    }}
                  />
                  {formik.errors.gender && formik.touched.gender && (
                    <div style={{ color: "red", fontSize: 12 }}>{formik.errors.gender}</div>
                  )}
                </Grid>
                <Grid item xs={12} xl={3}>
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                      {t("teachers.reg_date", "Date d'inscription")}
                  </SoftTypography>
                  <SoftInput type="date" name="regDate" value={formik.values.regDate} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                  {formik.touched.regDate && formik.errors.regDate ? (
                    <div className="error-message">
                      {formik.errors.regDate}
                    </div>
                  ) : null}
                </Grid>
                <Grid item xs={12} xl={3}>
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                      {t("teachers.degree", "Diplôme")}
                  </SoftTypography>
                  <SoftInput type="text" name="degree" value={formik.values.degree} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                  {formik.touched.degree && formik.errors.degree ? (
                    <div className="error-message">
                      {formik.errors.degree}
                    </div>
                  ) : null}
                </Grid>
                <Grid item xs={12} xl={3}>
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                      {t("teachers.phone", "Téléphone")}
                  </SoftTypography>
                  <SoftInput type="text" name="phone" value={formik.values.phone} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                  {formik.touched.phone && formik.errors.phone ? (
                    <div className="error-message">
                      {formik.errors.phone}
                    </div>
                  ) : null}
                </Grid>
                <Grid item xs={12} xl={3}>
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                      {t("teachers.dob", "Date de naissance")}
                  </SoftTypography>
                  <SoftInput type="date" name="dob" value={formik.values.dob} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                  {formik.touched.dob && formik.errors.dob ? (
                    <div className="error-message">
                      {formik.errors.dob}
                    </div>
                  ) : null}
                </Grid>
                <Grid item xs={12} xl={3}>
                  <label>{t("teachers.status", "Sélectionnez le statut")}</label>
                  <Select2Field
                    name="status"
                    options={[
                      { id: "", text: t("teachers.status_placeholder", "Sélectionner le statut") },
                      { id: "Actif", text: t("status.active", "Actif") },
                      { id: "Inactif", text: t("status.inactive", "Inactif") },
                    ]}
                    placeholder={t("teachers.status_placeholder", "Sélectionner le statut")}
                    value={formik.values.status}
                    onChange={(val) => {
                      formik.setFieldValue("status", val);
                    }}
                  />
                  {formik.errors.status && formik.touched.status && (
                    <div style={{ color: "red", fontSize: 12 }}>{formik.errors.status}</div>
                  )}
                </Grid>
                <Grid item xs={12} xl={2}>
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                      {t("teachers.certificate", "Certificat")}
                  </SoftTypography>
                  <SoftInput type="text" name="certificate" value={formik.values.certificate} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                  {formik.touched.certificate && formik.errors.certificate ? (
                    <div className="error-message">
                      {formik.errors.certificate}
                    </div>
                  ) : null}
                </Grid>
                {!edit && (
                  <Grid item xs={12} xl={2}>
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                        {t("teachers.reg_number", "Numéro de registre")}
                    </SoftTypography>
                    <SoftInput
                      type="text"
                      name="regNumber"
                      value={formik.values.regNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.regNumber && formik.errors.regNumber ? (
                      <div className="error-message">
                        {formik.errors.regNumber}
                      </div>
                    ) : null}
                  </Grid>
                )}
                <Grid item xs={12} xl={2}>
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                      {t("teachers.identification", "Numéro d'identification")}
                  </SoftTypography>
                  <SoftInput type="text" name="identificationNumber" value={formik.values.identificationNumber} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                  {formik.touched.identificationNumber && formik.errors.identificationNumber ? (
                    <div className="error-message">
                      {formik.errors.identificationNumber}
                    </div>
                  ) : null}
                </Grid>
                <Grid item xs={12} xl={3}>
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                      {t("teachers.integration_order", "Numéro de commande d'intégration")}
                  </SoftTypography>
                  <SoftInput type="text" name="integrationOrderNumber" value={formik.values.integrationOrderNumber} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                  {formik.touched.integrationOrderNumber && formik.errors.integrationOrderNumber ? (
                    <div className="error-message">
                      {formik.errors.integrationOrderNumber}
                    </div>
                  ) : null}
                </Grid>
                <Grid item xs={12} xl={3}>
                  <label>{t("teachers.recruiter_select", "Sélectionner un recruteur")}</label>
                  <Select2Field
                    name="recruiterId"
                    options={[
                      { id: "", text: t("teachers.recruiter_select", "Sélectionner un recruteur") },
                      ...recruiterOptions,
                    ]}
                    placeholder={t("teachers.recruiter_select", "Sélectionner un recruteur")}
                    value={formik.values.recruiterId}
                    onChange={(val) => {
                      formik.setFieldValue("recruiterId", val);
                    }}
                  />
                </Grid>

                <Grid item xs={12} xl={3}>
                  <SoftButton type="submit" variant="gradient" color="info" fullWidth style={{
                    marginTop: "30px",
                    ...(edit
                      ? { width: "48%", marginLeft: "2%" }
                      : { width: "100%" })
                  }}>
                    {formik.isSubmitting ? t("teachers.loading", "Chargement...") : t("teachers.save", "Enregistrer")}
                  </SoftButton>
                  {edit && (
                    <SoftButton type="button" variant="gradient" color="secondary" fullWidth 
                      style={{marginTop: '30px', width: '48%', marginLeft: '2%'}}
                      onClick={() => {
                        formik.resetForm();
                        setEdit(false);
                      }}
                    >
                      {t("teachers.cancel", "Annuler")}
                    </SoftButton>
                  )}
                </Grid>
              </Grid>
            </form>
            </Collapse>
          </Card>}
          <Card sx={CARD_SHELL_SX}>
            <SoftBox
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              gap={2}
              sx={{
                px: { xs: 2, md: 2.5 },
                py: 2,
                borderBottom: `1px solid ${figmaTokens.cardHeaderRule}`,
              }}
            >
              <SoftBox>
                <SoftTypography variant="h6" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, letterSpacing: "-0.02em" }}>
                  {t("teachers.list", "Liste des enseignants")}
                </SoftTypography>
                <SoftTypography
                  variant="body2"
                  sx={{ display: "block", mt: 0.75, color: figmaTokens.textSecondary, maxWidth: 520 }}
                >
                  {t(
                    "teachers.list_subtitle",
                    "Vue d’ensemble du personnel enseignant et actions rapides."
                  )}
                </SoftTypography>
              </SoftBox>
              <SoftBox
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
              >
                <Tooltip title={t("teachers.refresh", "Actualiser la liste")}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => refreshAll()}
                      aria-label={t("teachers.refresh", "Actualiser la liste")}
                      sx={{
                        color: figmaTokens.accent,
                        border: `1px solid ${figmaTokens.borderStrong}`,
                        borderRadius: "12px",
                        "&:hover": {
                          bgcolor: "rgba(102, 126, 234, 0.08)",
                          borderColor: figmaTokens.accent,
                        },
                      }}
                    >
                      <RefreshOutlined fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </SoftBox>
            </SoftBox>
            <SoftBox
              sx={{
                px: { xs: 2, sm: 3 },
                pb: 2.5,
                pt: 0,
              }}
            >
              <SoftBox
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                gap={1.5}
                mb={2}
              >
                <SoftBox>
                  <SoftTypography
                    variant="overline"
                    sx={{
                      fontWeight: 800,
                      letterSpacing: "0.12em",
                      color: figmaTokens.textMuted,
                      fontSize: "0.68rem",
                    }}
                  >
                    {t("teachers.pipeline.kicker", "ENROLLMENT PIPELINE")}
                  </SoftTypography>
                  <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, mt: 0.5, maxWidth: 560 }}>
                    {t(
                      "teachers.pipeline.hint",
                      "Track enrollment from registration through assignment and first attendance. Select a stage to filter the list."
                    )}
                  </SoftTypography>
                </SoftBox>
                <Chip
                  label={t("teachers.pipeline.enrolled_total", "{{count}} enrolled").replace(
                    "{{count}}",
                    String(enrolledTotal)
                  )}
                  sx={{
                    fontWeight: 800,
                    height: 32,
                    borderRadius: "10px",
                    bgcolor: "rgba(102, 126, 234, 0.1)",
                    color: figmaTokens.accent,
                    border: "1px solid rgba(102, 126, 234, 0.25)",
                  }}
                />
              </SoftBox>

              {enrolledTotal > 0 && (
                <SoftBox mb={2.5}>
                  <SoftBox
                    sx={{
                      display: "flex",
                      height: 12,
                      borderRadius: "999px",
                      overflow: "hidden",
                      bgcolor: figmaTokens.pageBg,
                      border: `1px solid ${figmaTokens.borderSubtle}`,
                    }}
                  >
                    {pipelineSegments.map((seg) =>
                      seg.count > 0 ? (
                        <Tooltip
                          key={seg.key}
                          title={`${t(seg.labelKey, seg.fallback)}: ${seg.count} (${seg.pct}%)`}
                        >
                          <SoftBox
                            sx={{
                              width: `${Math.max(seg.pct, 2)}%`,
                              bgcolor: seg.color,
                              transition: "width 0.35s ease",
                            }}
                          />
                        </Tooltip>
                      ) : null
                    )}
                  </SoftBox>
                  <SoftBox display="flex" flexWrap="wrap" gap={{ xs: 1.5, md: 2.5 }} mt={1.25}>
                    {pipelineSegments.map((seg) => (
                      <SoftBox key={seg.key} display="flex" alignItems="center" gap={0.75}>
                        <SoftBox
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor: seg.color,
                            flexShrink: 0,
                          }}
                        />
                        <SoftTypography variant="caption" sx={{ color: figmaTokens.textSecondary, fontWeight: 600 }}>
                          {t(seg.labelKey, seg.fallback)}:{" "}
                          <SoftBox component="span" sx={{ fontWeight: 800, color: figmaTokens.textPrimary }}>
                            {seg.count}
                          </SoftBox>
                        </SoftTypography>
                      </SoftBox>
                    ))}
                  </SoftBox>
                </SoftBox>
              )}

              <SoftBox
                sx={{
                  display: "grid",
                  gap: 1.5,
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(4, minmax(0, 1fr))",
                  },
                }}
              >
                {TEACHER_CATEGORIES.map((cat) => {
                  const selected = category === cat.value;
                  const count = categoryCounts[cat.value] ?? 0;
                  return (
                    <SoftBox
                      key={cat.value}
                      component="button"
                      type="button"
                      onClick={() => handleCategoryChange(cat.value)}
                      sx={{
                        m: 0,
                        p: 2,
                        textAlign: "left",
                        cursor: "pointer",
                        borderRadius: "14px",
                        border: selected
                          ? `2px solid ${cat.accent}`
                          : `1px solid ${figmaTokens.borderSubtle}`,
                        bgcolor: selected ? alpha(cat.accent, 0.06) : figmaTokens.surface,
                        boxShadow: selected
                          ? `0 12px 32px ${alpha(cat.accent, 0.18)}`
                          : "0 8px 24px rgba(15, 23, 42, 0.05)",
                        transition: "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          borderColor: cat.accent,
                          boxShadow: `0 14px 36px ${alpha(cat.accent, 0.14)}`,
                        },
                      }}
                    >
                      <SoftBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                        <SoftBox
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: alpha(cat.accent, 0.14),
                            color: cat.accent,
                            flexShrink: 0,
                          }}
                        >
                          <Icon fontSize="small">{cat.icon}</Icon>
                        </SoftBox>
                        {selected && (
                          <Chip
                            size="small"
                            label={t("teachers.pipeline.active_filter", "Active")}
                            sx={{
                              height: 22,
                              fontSize: "0.65rem",
                              fontWeight: 800,
                              bgcolor: cat.accent,
                              color: "#fff",
                            }}
                          />
                        )}
                      </SoftBox>
                      <SoftTypography
                        variant="button"
                        sx={{
                          display: "block",
                          mt: 1.25,
                          fontWeight: 800,
                          color: figmaTokens.textPrimary,
                          lineHeight: 1.35,
                        }}
                      >
                        {t(cat.labelKey, cat.fallback)}
                      </SoftTypography>
                      <SoftTypography
                        variant="h4"
                        fontWeight="bold"
                        sx={{ color: cat.accent, mt: 0.5, letterSpacing: "-0.03em" }}
                      >
                        {count}
                      </SoftTypography>
                      <SoftTypography variant="caption" sx={{ display: "block", mt: 0.5, color: figmaTokens.textMuted, fontWeight: 600 }}>
                        {shareLabel(count)}
                      </SoftTypography>
                      <SoftTypography variant="caption" sx={{ display: "block", mt: 0.75, color: figmaTokens.textSecondary, lineHeight: 1.45 }}>
                        {t(cat.descKey, cat.descFallback)}
                      </SoftTypography>
                    </SoftBox>
                  );
                })}
              </SoftBox>
            </SoftBox>
            <SoftBox px={{ xs: 1.25, md: 2 }} pb={1}>
              <SoftBox
                sx={{
                  px: 2,
                  py: 1.25,
                  mb: 1.5,
                  borderRadius: "12px",
                  bgcolor: "rgba(102, 126, 234, 0.07)",
                  border: `1px solid rgba(102, 126, 234, 0.18)`,
                }}
              >
                <SoftTypography variant="body2" sx={{ fontWeight: 700, color: figmaTokens.textPrimary }}>
                  {viewingLabel}
                </SoftTypography>
                {activeCategory.value !== 1 && (
                  <SoftTypography variant="caption" sx={{ display: "block", mt: 0.5, color: figmaTokens.textSecondary }}>
                    {t(activeCategory.descKey, activeCategory.descFallback)}
                  </SoftTypography>
                )}
              </SoftBox>
              {teachersTableRangeLabel && (
                <SoftTypography
                  variant="caption"
                  sx={{
                    display: "block",
                    mb: 1.5,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    color: figmaTokens.textMuted,
                    textTransform: "uppercase",
                  }}
                >
                  {teachersTableRangeLabel}
                </SoftTypography>
              )}
              <SoftBox
                sx={{
                  mb: 1.5,
                  borderRadius: "12px",
                  border: `1px solid ${figmaTokens.borderSubtle}`,
                  bgcolor: figmaTokens.pageBg,
                  overflow: "hidden",
                }}
              >
                <SoftBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  px={1.5}
                  py={1.25}
                  sx={{
                    borderBottom: filtersMinimized ? "none" : `1px solid ${figmaTokens.borderSubtle}`,
                  }}
                >
                  <SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <SoftTypography variant="caption" fontWeight="bold">
                      {t("teachers.server_filters", "Search & filters")}
                    </SoftTypography>
                    {listFilterActive && (
                      <Chip
                        size="small"
                        label={t("teachers.filters_active_count", "{{count}} active").replace(
                          "{{count}}",
                          String(appliedFilterCount)
                        )}
                        sx={{ height: 22, fontSize: "0.65rem", fontWeight: 700 }}
                      />
                    )}
                  </SoftBox>
                  <SoftBox display="flex" alignItems="center" gap={0.5}>
                    {!filtersMinimized && (
                      <SoftButton
                        type="button"
                        variant="text"
                        color="info"
                        size="small"
                        onClick={() => setFiltersAdvancedOpen((v) => !v)}
                      >
                        {filtersAdvancedOpen
                          ? t("teachers.filters_hide", "Hide advanced")
                          : t("teachers.filters_show", "Advanced filters")}
                      </SoftButton>
                    )}
                    <Tooltip
                      title={
                        filtersMinimized
                          ? t("teachers.filters_expand", "Show filters")
                          : t("teachers.filters_minimize", "Minimize filters")
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() => setFiltersMinimized((v) => !v)}
                        aria-label={
                          filtersMinimized
                            ? t("teachers.filters_expand", "Show filters")
                            : t("teachers.filters_minimize", "Minimize filters")
                        }
                        sx={{
                          color: figmaTokens.accent,
                          border: `1px solid ${figmaTokens.borderSubtle}`,
                          borderRadius: "10px",
                        }}
                      >
                        {filtersMinimized ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </SoftBox>
                </SoftBox>
                <Collapse in={!filtersMinimized} timeout="auto">
                <SoftBox p={1.5} pt={filtersMinimized ? 0 : 1.5}>
                <SoftBox display="flex" alignItems="center" gap={1} mb={1.5}>
                  <SoftBox sx={{ position: "relative", flex: 1 }}>
                    <SearchOutlined
                      sx={{
                        position: "absolute",
                        left: 12,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: "1.1rem",
                        color: figmaTokens.textMuted,
                        pointerEvents: "none",
                      }}
                    />
                    <SoftInput
                      type="search"
                      name="search"
                      value={draftFilters.search}
                      onChange={(e) => updateDraftFilter("search", e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      placeholder={t(
                        "teachers.search_placeholder",
                        "Search all columns (matricule, name, phone, email…)"
                      )}
                      sx={{ "& input": { pl: "2.5rem" } }}
                    />
                  </SoftBox>
                </SoftBox>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6} md={3}>
                    <SoftTypography component="label" sx={filterLabelSx}>
                      {t("teachers.filter.type", "Type")}
                    </SoftTypography>
                    <Select2Field
                      name="filterIsTeacher"
                      options={[
                        { id: "", text: t("teachers.type.all", "All") },
                        { id: "true", text: t("teachers.type.teacher", "Teachers") },
                        { id: "false", text: t("teachers.type.staff", "Staff") },
                      ]}
                      value={draftFilters.isTeacher}
                      onChange={(val) => updateDraftFilter("isTeacher", val)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SoftTypography component="label" sx={filterLabelSx}>
                      {t("teachers.reg_number", "Matricule")}
                    </SoftTypography>
                    <SoftInput
                      value={draftFilters.regNumber}
                      onChange={(e) => updateDraftFilter("regNumber", e.target.value)}
                      placeholder={t("teachers.reg_number", "Matricule")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SoftTypography component="label" sx={filterLabelSx}>
                      {t("teachers.firstname", "Prénom")}
                    </SoftTypography>
                    <SoftInput
                      value={draftFilters.preName}
                      onChange={(e) => updateDraftFilter("preName", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SoftTypography component="label" sx={filterLabelSx}>
                      {t("teachers.name", "Nom")}
                    </SoftTypography>
                    <SoftInput
                      value={draftFilters.name}
                      onChange={(e) => updateDraftFilter("name", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SoftTypography component="label" sx={filterLabelSx}>
                      {t("home.table.gender", "Gender")}
                    </SoftTypography>
                    <Select2Field
                      name="filterGender"
                      options={[
                        { id: "", text: t("map.filter.all_genders", "All genders") },
                        { id: "Homme", text: t("gender.male", "Homme") },
                        { id: "Femme", text: t("gender.female", "Femme") },
                      ]}
                      value={draftFilters.gender}
                      onChange={(val) => updateDraftFilter("gender", val)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SoftTypography component="label" sx={filterLabelSx}>
                      {t("teachers.table.degree", "Diplôme")}
                    </SoftTypography>
                    <Select2Field
                      name="filterDegree"
                      options={[
                        { id: "", text: t("map.filter.all_degrees", "All degrees") },
                        ...MAP_TEACHER_DEGREES.map((id) => ({ id, text: id })),
                      ]}
                      value={draftFilters.degree}
                      onChange={(val) => updateDraftFilter("degree", val)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SoftTypography component="label" sx={filterLabelSx}>
                      {t("teachers.table.status", "Statut")}
                    </SoftTypography>
                    <SoftInput
                      value={draftFilters.status}
                      onChange={(e) => updateDraftFilter("status", e.target.value)}
                      placeholder={t("teachers.status_filter_placeholder", "e.g. Contractor")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SoftTypography component="label" sx={filterLabelSx}>
                      {t("teachers.phone", "Téléphone")}
                    </SoftTypography>
                    <SoftInput
                      type="tel"
                      value={draftFilters.phone}
                      onChange={(e) => updateDraftFilter("phone", e.target.value)}
                      placeholder={t("teachers.phone_placeholder", "Phone number")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SoftTypography component="label" sx={filterLabelSx}>
                      {t("teachers.whatsapp", "WhatsApp")}
                    </SoftTypography>
                    <SoftInput
                      type="tel"
                      value={draftFilters.whatsapp}
                      onChange={(e) => updateDraftFilter("whatsapp", e.target.value)}
                      placeholder={t("teachers.whatsapp_placeholder", "WhatsApp number")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SoftTypography component="label" sx={filterLabelSx}>
                      {t("teachers.email", "Email")}
                    </SoftTypography>
                    <SoftInput
                      type="email"
                      value={draftFilters.email}
                      onChange={(e) => updateDraftFilter("email", e.target.value)}
                      placeholder={t("teachers.email_placeholder", "Email address")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SoftTypography component="label" sx={filterLabelSx}>
                      {t("teachers.recruiter_select", "Recruteur")}
                    </SoftTypography>
                    <Select2Field
                      name="filterRecruiterId"
                      options={[
                        { id: "", text: t("teachers.recruiter_all", "All recruiters") },
                        ...recruiterOptions,
                      ]}
                      value={draftFilters.recruiterId}
                      onChange={(val) => updateDraftFilter("recruiterId", val)}
                    />
                  </Grid>
                </Grid>
                <Collapse in={filtersAdvancedOpen} timeout="auto" unmountOnExit>
                  <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <SoftTypography component="label" sx={filterLabelSx}>
                        {t("teachers.identification", "ID number")}
                      </SoftTypography>
                      <SoftInput
                        value={draftFilters.identificationNumber}
                        onChange={(e) => updateDraftFilter("identificationNumber", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <SoftTypography component="label" sx={filterLabelSx}>
                        {t("teachers.recruiter_name", "Recruiter name")}
                      </SoftTypography>
                      <SoftInput
                        value={draftFilters.recruiter}
                        onChange={(e) => updateDraftFilter("recruiter", e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Collapse>
                <SoftBox display="flex" gap={1} flexWrap="wrap" mt={1.5}>
                  <SoftButton variant="gradient" color="info" onClick={handleApplyFilters}>
                    {t("teacher_license.apply", "Apply")}
                  </SoftButton>
                  <SoftButton variant="outlined" color="secondary" onClick={resetFilters} disabled={!listFilterActive}>
                    {t("teacher_license.reset", "Reset")}
                  </SoftButton>
                  <SoftButton
                    type="button"
                    variant="gradient"
                    color="dark"
                    onClick={handleExportFiltered}
                    disabled={totalResults === 0}
                    startIcon={<FileDownloadOutlined />}
                  >
                    {t("teachers.export_filtered", "Export filtered")}
                  </SoftButton>
                </SoftBox>
                <SoftTypography variant="caption" sx={{ display: "block", mt: 1, color: figmaTokens.textMuted }}>
                  {t(
                    "teachers.server_filters_hint",
                    "Filters run on the server. Tab counts use stats.categoryCounts from the API."
                  )}
                </SoftTypography>
                {activeFilterChips.length > 0 && (
                  <SoftBox display="flex" flexWrap="wrap" gap={0.75} mt={1.5}>
                    {activeFilterChips.map(({ key, value }) => (
                      <Chip
                        key={key}
                        size="small"
                        label={`${key}: ${value}`}
                        sx={{ fontWeight: 600, fontSize: "0.72rem" }}
                      />
                    ))}
                  </SoftBox>
                )}
                </SoftBox>
                </Collapse>
                {filtersMinimized && activeFilterChips.length > 0 && (
                  <SoftBox px={1.5} pb={1.25} display="flex" flexWrap="wrap" gap={0.75}>
                    {activeFilterChips.slice(0, 4).map(({ key, value }) => (
                      <Chip
                        key={key}
                        size="small"
                        variant="outlined"
                        label={`${key}: ${value}`}
                        sx={{ fontSize: "0.68rem" }}
                      />
                    ))}
                    {activeFilterChips.length > 4 && (
                      <Chip
                        size="small"
                        label={`+${activeFilterChips.length - 4}`}
                        sx={{ fontSize: "0.68rem" }}
                      />
                    )}
                  </SoftBox>
                )}
              </SoftBox>
              {listFilterActive && (
                <SoftTypography variant="caption" sx={{ display: "block", mb: 1, color: figmaTokens.textSecondary }}>
                  {t("teachers.filtered_count_server", "{{count}} matching (server-side)").replace(
                    "{{count}}",
                    String(totalResults)
                  )}
                </SoftTypography>
              )}
              <TableContainer component={Paper} elevation={0} sx={teachersTableShellSx}>
                <Table
                  size="small"
                  stickyHeader
                  sx={{
                    minWidth: 1500,
                    ...directoryTableHeadStickyOverridesSx,
                    ...directoryTableBodyCellDefaultsSx,
                    "& .MuiTableRow-root:hover .MuiTableCell-root": {
                      bgcolor: "rgba(102, 126, 234, 0.045)",
                    },
                  }}
                  aria-label={t("teachers.list", "Liste des enseignants")}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 120 }}>
                        {t("teachers.table.reg", "Matricule de l'enseignant")}
                      </TableCell>
                      <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 200 }}>
                        {t(
                          "teachers.table.name",
                          "Nom et prénoms de l'enseignant"
                        )}
                      </TableCell>
                      <TableCell sx={directoryTableHeaderCellSx}>
                        {t("teachers.table.gender", "Sexe de l'enseignant")}
                      </TableCell>
                      <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 120 }}>
                        {t("teachers.table.status", "Statut de l'enseignant")}
                      </TableCell>
                      <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 110 }}>
                        {t("teachers.table.type", "Type")}
                      </TableCell>
                      <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 130 }}>
                        {t("teachers.table.license", "License")}
                      </TableCell>
                      <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 140 }}>
                        {t("teachers.table.degree", "Diplôme de l'enseignant")}
                      </TableCell>
                      <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 120 }}>
                        {t("teachers.table.phone", "Téléphone")}
                      </TableCell>
                      <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 120 }}>
                        {t("teachers.table.whatsapp", "WhatsApp")}
                      </TableCell>
                      <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 160 }}>
                        {t("teachers.table.email", "Email")}
                      </TableCell>
                      <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 120 }}>{t("teachers.recruiter", "Recruteur")}</TableCell>
                      <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 140 }}>
                        {t("teachers.table.last_checkin", "Last check-in")}
                      </TableCell>
                      <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 110 }}>
                        {t("teachers.table.in_radius", "In radius")}
                      </TableCell>
                      {showStatusColumns && (
                        <>
                          <TableCell sx={directoryTableHeaderCellSx}>
                            {t("teachers.table.allocated", "Assigned")}
                          </TableCell>
                          <TableCell sx={directoryTableHeaderCellSx}>
                            {t("teachers.table.attendance", "Attendance")}
                          </TableCell>
                        </>
                      )}
                      <TableCell align="right" sx={directoryTableHeaderCellSx}>
                        {t("teachers.table.action", "Action")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teachersList.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={tableColSpan} align="center" sx={{ py: 8, color: figmaTokens.textSecondary, fontSize: "0.9rem" }}>
                          <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary }}>
                            {listFilterActive
                              ? t("teachers.none_filtered", "No teachers match the selected filters.")
                              : t(
                                  "teachers.none",
                                  "Aucun enseignant à afficher."
                                )}
                          </SoftTypography>
                        </TableCell>
                      </TableRow>
                    )}
                    {teachersList.map((row) => {
                      const allocated = isTruthyFlag(row?.isAllocated ?? row?.IsAllocated);
                      const hasAttendance = isTruthyFlag(row?.hasAttendance ?? row?.HasAttendance);
                      const teacherId = row.id ?? row.Id;
                      const isTeacher = readIsTeacher(row?.isTeacher ?? row?.IsTeacher);
                      const isLicensed = readIsLicensed(row?.isLicensed ?? row?.IsLicensed);
                      const attMeta = attendanceSummary.get(String(teacherId));
                      return (
                      <TableRow
                        key={row.id ?? row.Id}
                        hover
                        sx={{
                          "&:nth-of-type(even) td": { bgcolor: "rgba(247, 249, 252, 0.65)" },
                          transition: "background-color 0.15s ease",
                        }}
                      >
                        <TableCell sx={{ fontWeight: 700, color: figmaTokens.textPrimary }}>
                          <SoftTypography
                            variant="body2"
                            sx={{
                              fontVariantNumeric: "tabular-nums",
                              fontFeatureSettings: '"tnum"',
                              fontSize: "0.8125rem",
                              fontWeight: 700,
                              color: figmaTokens.textPrimary,
                            }}
                          >
                            {row.regNumber ?? "—"}
                          </SoftTypography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <TeacherProfileLink
                            id={teacherId}
                            regNumber={row.regNumber}
                            style={{ textDecoration: "none" }}
                          >
                            <SoftBox display="flex" alignItems="center" gap={1.25} minWidth={0}>
                              <TeacherAvatar teacher={row} size="xs" />
                              <SoftTypography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.8125rem",
                                  color: figmaTokens.accent,
                                  letterSpacing: "-0.01em",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  "&:hover": { color: figmaTokens.accentEnd, textDecoration: "underline", textUnderlineOffset: 3 },
                                }}
                              >
                                {[row.name, row.preName].filter(Boolean).join(" ") ||
                                  "—"}
                              </SoftTypography>
                            </SoftBox>
                          </TeacherProfileLink>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={getGenderLabel(row.gender)}
                            sx={teacherGenderChipSx(row.gender)}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={String(row.status ?? "—")}
                            sx={teacherStatusChipSx(row.status)}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={
                              isTeacher
                                ? t("teachers.type.teacher", "Teacher")
                                : t("teachers.type.staff", "Staff")
                            }
                            sx={teacherTypeChipSx(isTeacher)}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={
                              isLicensed
                                ? t("teachers.license.licensed", "Licensed")
                                : t("teachers.license.pending", "Pending activation")
                            }
                            sx={teacherLicenseChipSx(isLicensed)}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 160,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={row.degree ?? ""}
                        >
                          <SoftTypography variant="body2" sx={{ fontWeight: 500, fontSize: "0.8125rem", color: figmaTokens.textBody }}>
                            {row.degree ?? "—"}
                          </SoftTypography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <SoftTypography
                            variant="body2"
                            sx={{ fontSize: "0.8125rem", color: figmaTokens.textBody, fontVariantNumeric: "tabular-nums" }}
                          >
                            {contactCellValue(row, "phone", "Phone")}
                          </SoftTypography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <SoftTypography
                            variant="body2"
                            sx={{ fontSize: "0.8125rem", color: figmaTokens.textBody, fontVariantNumeric: "tabular-nums" }}
                          >
                            {contactCellValue(row, "whatsapp", "Whatsapp", "WhatsApp")}
                          </SoftTypography>
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={
                            (() => {
                              const email = contactCellValue(row, "email", "Email");
                              return email !== "—" ? email : undefined;
                            })()
                          }
                        >
                          <SoftTypography variant="body2" sx={{ fontSize: "0.8125rem", color: figmaTokens.textBody }}>
                            {contactCellValue(row, "email", "Email")}
                          </SoftTypography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 160 }}>
                          <SoftTypography variant="body2" sx={{ fontSize: "0.8125rem", color: figmaTokens.textBody }}>
                            {row.recruiter || row.Recruiter || "—"}
                          </SoftTypography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <SoftTypography variant="body2" sx={{ fontSize: "0.8125rem", color: figmaTokens.textBody }}>
                            {attMeta?.lastCheckIn
                              ? String(attMeta.lastCheckIn).replace("T", " ").slice(0, 16)
                              : "—"}
                          </SoftTypography>
                        </TableCell>
                        <TableCell>
                          {attMeta?.inRadius === true ? (
                            <Chip
                              size="small"
                              label={t("map.kpi.in_radius", "In radius")}
                              sx={teacherBoolChipSx(true)}
                            />
                          ) : attMeta?.inRadius === false ? (
                            <Chip
                              size="small"
                              label={t("map.kpi.out_radius", "Out of radius")}
                              sx={teacherBoolChipSx(false)}
                            />
                          ) : (
                            <SoftTypography variant="body2" sx={{ color: figmaTokens.textMuted }}>
                              —
                            </SoftTypography>
                          )}
                        </TableCell>
                        {showStatusColumns && (
                          <>
                            <TableCell>
                              <Chip
                                size="small"
                                label={
                                  allocated
                                    ? t("teachers.allocated.yes", "Yes")
                                    : t("teachers.allocated.no", "No")
                                }
                                sx={teacherBoolChipSx(allocated)}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={
                                  hasAttendance
                                    ? t("teachers.attendance.yes", "Yes")
                                    : t("teachers.attendance.no", "No")
                                }
                                sx={teacherBoolChipSx(hasAttendance)}
                              />
                            </TableCell>
                          </>
                        )}
                        <TableCell align="right">
                          {teacherId ? (
                            <Tooltip title={t("teacher_detail.open_profile", "View profile")}>
                              <IconButton
                                size="small"
                                component={Link}
                                to={teacherDetailPath(teacherId, row.regNumber) || "#"}
                                aria-label={t("teacher_detail.open_profile", "View profile")}
                                sx={{
                                  borderRadius: "10px",
                                  mr: 0.5,
                                  "&:hover": {
                                    bgcolor: alpha(figmaTokens.gradientStart, 0.08),
                                  },
                                }}
                              >
                                <PersonOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {!readOnly && allocated && teacherId ? (
                            <Tooltip title={t("map.open_teacher_map", "View on map")}>
                              <IconButton
                                size="small"
                                component={Link}
                                to={`/carte-enseignants?teacherId=${teacherId}${
                                  row.regNumber
                                    ? `&regNumber=${encodeURIComponent(row.regNumber)}`
                                    : ""
                                }`}
                                aria-label={t("map.open_teacher_map", "View on map")}
                                sx={{
                                  borderRadius: "10px",
                                  mr: 0.5,
                                  "&:hover": {
                                    bgcolor: alpha(figmaTokens.gradientStart, 0.08),
                                  },
                                }}
                              >
                                <MapOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {!readOnly && <Tooltip
                            title={t("teachers.edit_row", "Modifier")}
                          >
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEdit(row)}
                              aria-label={t("teachers.edit_row", "Modifier")}
                              sx={{
                                borderRadius: "10px",
                                "&:hover": {
                                  bgcolor: alpha(figmaTokens.gradientStart, 0.08),
                                },
                              }}
                            >
                              <EditOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>}
                        </TableCell>
                      </TableRow>
                    );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePaginationBar
                page={page}
                totalCount={totalResults}
                pageSize={pageSize}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeLabel={t("pagination.page_size", "Rows per page")}
              />
            </SoftBox>
          </Card>
        </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Enseignantes;




